import React, { useState, useEffect, useCallback } from 'react';
import { FiMessageSquare, FiCornerDownRight, FiSend, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import questionService from '../../services/question.service';

const QASection = ({ productId, isAuthenticated, isMerchant = false }) => {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [questionText, setQuestionText] = useState('');
  const [answerTexts, setAnswerTexts] = useState({});
  const [showAnswerFor, setShowAnswerFor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      const { data } = await questionService.getByProduct(productId, { page, limit: 5 });
      setQuestions(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error('Failed to load Q&A:', err);
    } finally {
      setLoading(false);
    }
  }, [productId, page]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const handleAskQuestion = async () => {
    if (!questionText.trim()) return;
    setSubmitting(true);
    try {
      await questionService.ask(productId, questionText.trim());
      setQuestionText('');
      toast.success('Question posted!');
      setPage(1);
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswer = async (questionId) => {
    const text = answerTexts[questionId];
    if (!text?.trim()) return;
    setSubmitting(true);
    try {
      await questionService.answer(productId, questionId, text.trim());
      setAnswerTexts((prev) => ({ ...prev, [questionId]: '' }));
      setShowAnswerFor(null);
      toast.success('Answer posted!');
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post answer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4">
      <h5 className="mb-3">
        <FiMessageSquare className="me-2" />
        Customer Questions & Answers
        {total > 0 && <span className="text-muted fs-6 ms-2">({total})</span>}
      </h5>

      {/* Ask a question */}
      {isAuthenticated ? (
        <div className="d-flex gap-2 mb-4">
          <input
            type="text"
            className="form-control"
            placeholder="Have a question? Ask here..."
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAskQuestion()}
            maxLength={1000}
          />
          <button
            className="btn btn-ebw-primary"
            onClick={handleAskQuestion}
            disabled={submitting || !questionText.trim()}
            style={{ whiteSpace: 'nowrap' }}
          >
            <FiSend size={16} className="me-1" /> Ask
          </button>
        </div>
      ) : (
        <p className="text-muted small mb-4">
          <a href="/login">Login</a> to ask a question.
        </p>
      )}

      {/* Questions list */}
      {loading ? (
        <p className="text-muted">Loading questions...</p>
      ) : questions.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <FiMessageSquare size={32} className="mb-2 d-block mx-auto" />
          <small>No questions yet. Be the first to ask!</small>
        </div>
      ) : (
        <div>
          {questions.map((q) => (
            <div key={q._id} className="mb-4 pb-3 border-bottom">
              {/* Question */}
              <div className="d-flex gap-2">
                <span className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>Q:</span>
                <div className="flex-grow-1">
                  <p className="mb-1">{q.body}</p>
                  <small className="text-muted">
                    {q.userName} · {new Date(q.createdAt).toLocaleDateString()}
                    {q.status === 'answered' && (
                      <span className="text-success ms-2"><FiCheckCircle size={12} /> Answered</span>
                    )}
                  </small>
                </div>
              </div>

              {/* Answers */}
              {q.answers?.length > 0 && (
                <div className="ms-4 mt-2">
                  {q.answers.map((a, idx) => (
                    <div key={idx} className="d-flex gap-2 mb-2">
                      <FiCornerDownRight size={14} className="text-muted mt-1 flex-shrink-0" />
                      <div>
                        <p className="mb-1 small">{a.body}</p>
                        <small className="text-muted">
                          {a.userName}
                          {a.isMerchant && (
                            <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem' }}>Seller</span>
                          )}
                          {' · '}{new Date(a.createdAt).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Answer button */}
              {isAuthenticated && (
                <div className="ms-4 mt-2">
                  {showAnswerFor === q._id ? (
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Write your answer..."
                        value={answerTexts[q._id] || ''}
                        onChange={(e) => setAnswerTexts((prev) => ({ ...prev, [q._id]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && handleAnswer(q._id)}
                        maxLength={2000}
                      />
                      <button
                        className="btn btn-sm btn-ebw-primary"
                        onClick={() => handleAnswer(q._id)}
                        disabled={submitting}
                      >
                        Reply
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setShowAnswerFor(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowAnswerFor(q._id)}
                    >
                      <FiCornerDownRight size={14} className="me-1" /> Answer
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Pagination */}
          {total > 5 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="small text-muted align-self-center">Page {page}</span>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page * 5 >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QASection;
